import { FIRST_PAGE } from 'data/global';
import { useCallback, useEffect, useRef, useState } from 'react';
import useTimer from './useTimer';
import { PageChangeEvent } from '@react-pdf-viewer/core';

const IDLE_TIME_PERIOD = 60; // in seconds

const useFilePreviewTrackTime = ({
  isModalOpen = false,
  sendReport,
  onDocumentViewed,
}: {
  isModalOpen?: boolean;
  sendReport: (time: number, currentPage: number) => void;
  onDocumentViewed: (time: number) => void;
}) => {
  const [isCounting, setIsCounting] = useState(false);
  const [isInIdle, setIsInIdle] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false); //file is loaded
  const [currentPage, setCurrentPage] = useState(+FIRST_PAGE);

  const totalTimeRef = useRef(0);

  const { time, idleTime, startTimer, endTimer, resetIdleTimer } = useTimer();

  const onDocumentLoad = useCallback(() => setIsLoaded(true), []);

  const updateTotalTime = useCallback(
    (time: number) => (totalTimeRef.current += time),
    [],
  );

  const start = useCallback(() => {
    if (!isLoaded) return;

    setIsCounting(true);
    setIsInIdle(false);

    document.addEventListener('wheel', resetIdleTimer);
    document.addEventListener('keydown', resetIdleTimer);
    document.addEventListener('mousemove', resetIdleTimer);

    startTimer();
  }, [isLoaded, resetIdleTimer, startTimer]);

  const end = useCallback(() => {
    if (!isLoaded) return;

    setIsCounting(false);

    document.removeEventListener('wheel', resetIdleTimer);
    document.removeEventListener('keydown', resetIdleTimer);
    document.removeEventListener('mousemove', resetIdleTimer);

    endTimer();
  }, [isLoaded, endTimer, resetIdleTimer]);

  const onPageChange = useCallback(
    (event: PageChangeEvent) => {
      if (isLoaded) {
        //event counter for currentPage starts with 0
        const page = event.currentPage + 1;

        if (currentPage !== page) {
          if (time) {
            sendReport(time, currentPage);
            updateTotalTime(time);
          }

          endTimer();
          startTimer();
          setCurrentPage(page);
        }
      }
    },
    [
      isLoaded,
      currentPage,
      time,
      endTimer,
      startTimer,
      sendReport,
      updateTotalTime,
    ],
  );

  // if in idle, wait for next action to start timer again
  useEffect(() => {
    if (isLoaded) {
      if (isInIdle) {
        document.addEventListener('wheel', start);
        document.addEventListener('keydown', start);
        document.addEventListener('mousemove', start);
      } else {
        document.removeEventListener('wheel', start);
        document.removeEventListener('keydown', start);
        document.removeEventListener('mousemove', start);
      }
    }
  }, [isInIdle, isLoaded, start]);

  // track time
  useEffect(() => {
    if (isLoaded) {
      if (!isModalOpen) {
        if (time) {
          sendReport(time, currentPage);
          updateTotalTime(time);
        }

        onDocumentViewed(totalTimeRef.current);

        setCurrentPage(+FIRST_PAGE);
        totalTimeRef.current = 0;
        setIsLoaded(false);
        end();
        return;
      }

      if (!isCounting && !isInIdle) {
        start();
      }

      if (idleTime >= IDLE_TIME_PERIOD) {
        if (time) {
          sendReport(time, currentPage);
          updateTotalTime(time);
        }

        end();
        setIsInIdle(true);
      }
    }
  }, [
    idleTime,
    isCounting,
    isInIdle,
    time,
    isLoaded,
    currentPage,
    isModalOpen,
    end,
    sendReport,
    start,
    updateTotalTime,
    onDocumentViewed,
  ]);

  return {
    isLoaded,
    onDocumentLoad,
    onPageChange,
  };
};

export default useFilePreviewTrackTime;
